import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation, useNavigate } from 'react-router-dom';
import HomeIcon from '@mui/icons-material/Home';
import PeopleIcon from '@mui/icons-material/People';
import ReceiptIcon from '@mui/icons-material/Receipt';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import HelpIcon from '@mui/icons-material/Help';
import FeedbackIcon from '@mui/icons-material/Feedback';
import LogoutIcon from '@mui/icons-material/Logout';
import PaymentIcon from '@mui/icons-material/Payment';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import DashboardIcon from '@mui/icons-material/Dashboard';
import MenuIcon from '@mui/icons-material/Menu';
import AudiotrackIcon from '@mui/icons-material/Audiotrack';
import ImageIcon from '@mui/icons-material/Image';
import VideocamIcon from '@mui/icons-material/Videocam';
import Login from './Login'; // eslint-disable-next-line no-unused-vars
import Home from './Home';
import User from './User';
import Owner from './Owner';
import Coupons from './Coupons';
import Issues from './Issues';
import Feedback from './Feedback';
import Orders from './Orders';
import Transactions from './Transactions';
import GeneratedImages from './GeneratedImages';
import ConvertedAudios from './ConvertedAudios';
import GeneratedVideos from './GeneratedVideos';
import { supabase, checkUser, restoreUserFromLocalStorage } from './supabaseClient';
import './App.css';
import './Login.css';

// Layout component to wrap authenticated routes
function MainLayout({ children }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  const toggleNavbar = () => {
    setIsExpanded(!isExpanded);
  };

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      if (window.innerWidth >= 768) {
        setIsExpanded(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div className="App">
      <div 
        className={`navbar ${isExpanded ? 'expanded' : 'collapsed'}`} 
        onMouseEnter={() => !isMobile && setIsExpanded(true)} 
        onMouseLeave={() => !isMobile && setIsExpanded(false)}
      >
        <div className="navbar-header">
          <img 
            src="https://ddtgdhehxhgarkonvpfq.supabase.co/storage/v1/object/public/user-uploads//matrix.png" 
            alt="Logo" 
            className="navbar-logo" 
          />
          {isExpanded && <div className="brand-text">MATRIX ADMIN</div>}
        </div>

        <div className="navbar-links">
          <NavbarItem 
            to="/" 
            icon={<DashboardIcon className="navbar-icon" />} 
            text="Dashboard" 
            isExpanded={isExpanded} 
            toggleNavbar={toggleNavbar} 
            isMobile={isMobile}
          />
          <NavbarItem 
            to="/users" 
            icon={<PeopleIcon className="navbar-icon" />} 
            text="Users" 
            isExpanded={isExpanded} 
            toggleNavbar={toggleNavbar} 
            isMobile={isMobile}
          />
          <NavbarItem 
            to="/orders" 
            icon={<ReceiptIcon className="navbar-icon" />} 
            text="Subscriptions" 
            isExpanded={isExpanded} 
            toggleNavbar={toggleNavbar} 
            isMobile={isMobile}
          />
          <NavbarItem 
            to="/transactions" 
            icon={<AccountBalanceWalletIcon className="navbar-icon" />} 
            text="Transactions" 
            isExpanded={isExpanded} 
            toggleNavbar={toggleNavbar} 
            isMobile={isMobile}
          />
          <NavbarItem 
            to="/generated-images" 
            icon={<ImageIcon className="navbar-icon" />} 
            text="Generated Images" 
            isExpanded={isExpanded} 
            toggleNavbar={toggleNavbar} 
            isMobile={isMobile}
          />
          <NavbarItem 
            to="/converted-audios" 
            icon={<AudiotrackIcon className="navbar-icon" />} 
            text="Converted Audios" 
            isExpanded={isExpanded} 
            toggleNavbar={toggleNavbar} 
            isMobile={isMobile}
          />
          <NavbarItem 
            to="/generated-videos" 
            icon={<VideocamIcon className="navbar-icon" />} 
            text="Generated Videos" 
            isExpanded={isExpanded} 
            toggleNavbar={toggleNavbar} 
            isMobile={isMobile}
          />
          <NavbarItem 
            to="/coupons" 
            icon={<LocalOfferIcon className="navbar-icon" />} 
            text="Coupons" 
            isExpanded={isExpanded} 
            toggleNavbar={toggleNavbar} 
            isMobile={isMobile}
          />
          <NavbarItem 
            to="/issues" 
            icon={<HelpIcon className="navbar-icon" />} 
            text="Issues" 
            isExpanded={isExpanded} 
            toggleNavbar={toggleNavbar} 
            isMobile={isMobile}
          />
          <NavbarItem 
            to="/feedback" 
            icon={<FeedbackIcon className="navbar-icon" />} 
            text="Feedback" 
            isExpanded={isExpanded} 
            toggleNavbar={toggleNavbar} 
            isMobile={isMobile}
          />
          
          {isExpanded && (
            <div className="navbar-footer">
              <div className="navbar-item" onClick={() => window.App.handleLogout()}>
                <LogoutIcon className="navbar-icon" />
                <span className="navbar-text">Logout</span>
              </div>
            </div>
          )}
        </div>
      </div>
      <div className={`content ${isExpanded ? 'expanded' : 'collapsed'}`}>
        <button onClick={toggleNavbar} className="navbar-toggle-button">
          <MenuIcon />
        </button>
        {children}
      </div>
    </div>
  );
}

function NavbarItem({ to, icon, text, isExpanded, toggleNavbar, isMobile }) {
  const location = useLocation();
  const navigate = useNavigate();
  const isActive = location.pathname === to;

  const handleClick = () => {
    if (isMobile) {
    toggleNavbar();
    }
    navigate(to);
  };

  return (
    <div className={`navbar-item ${isActive ? 'active' : ''} ${isExpanded ? 'visible' : 'hidden'}`} onClick={handleClick}>
      {icon}
      <span className="navbar-text">{text}</span>
    </div>
  );
}

function App() {
  const [session, setSession] = useState(null);

  // Attach logout handler to window for global access
  window.App = {
    handleLogout: async () => {
      await supabase.auth.signOut();
      setSession(null);
      localStorage.removeItem('user');
    }
  };

  useEffect(() => {
    const userFromStorage = restoreUserFromLocalStorage();
    if (userFromStorage) {
      setSession(userFromStorage);
    }

    checkUser().then(user => {
      if (user) {
        setSession(user);
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_IN') {
        setSession(session?.user);
      }
      if (event === 'SIGNED_OUT') {
        setSession(null);
        localStorage.removeItem('user');
      }
    });

    return () => {
      subscription?.unsubscribe();
    };
  }, []);

  return (
    <Router>
      <Routes>
        <Route path="/login" element={
          session ? <Navigate to="/" /> : <Login onLogin={() => checkUser().then(user => setSession(user))} />
        } />
        
        {/* Protected Routes */}
        <Route path="/" element={
          session ? (
            <MainLayout>
              <Home />
            </MainLayout>
          ) : (
            <Navigate to="/login" />
          )
        } />
        
        <Route path="/users" element={
          session ? (
            <MainLayout>
              <User />
            </MainLayout>
          ) : (
            <Navigate to="/login" />
          )
        } />
        
        <Route path="/owners" element={
          session ? (
            <MainLayout>
              <Owner />
            </MainLayout>
          ) : (
            <Navigate to="/login" />
          )
        } />
        
        <Route path="/coupons" element={
          session ? (
            <MainLayout>
              <Coupons />
            </MainLayout>
          ) : (
            <Navigate to="/login" />
          )
        } />
        
        <Route path="/issues" element={
          session ? (
            <MainLayout>
              <Issues />
            </MainLayout>
          ) : (
            <Navigate to="/login" />
          )
        } />
        
        <Route path="/feedback" element={
          session ? (
            <MainLayout>
              <Feedback />
            </MainLayout>
          ) : (
            <Navigate to="/login" />
          )
        } />
        
        <Route path="/orders" element={
          session ? (
            <MainLayout>
              <Orders />
            </MainLayout>
          ) : (
            <Navigate to="/login" />
          )
        } />
        
        <Route path="/transactions" element={
          session ? (
            <MainLayout>
              <Transactions />
            </MainLayout>
          ) : (
            <Navigate to="/login" />
          )
        } />
        
        <Route path="/generated-images" element={
          session ? (
            <MainLayout>
              <GeneratedImages />
            </MainLayout>
          ) : (
            <Navigate to="/login" />
          )
        } />
        
        <Route path="/converted-audios" element={
          session ? (
            <MainLayout>
              <ConvertedAudios />
            </MainLayout>
          ) : (
            <Navigate to="/login" />
          )
        } />
        
        <Route path="/generated-videos" element={
          session ? (
            <MainLayout>
              <GeneratedVideos />
            </MainLayout>
          ) : (
            <Navigate to="/login" />
          )
        } />
        
        {/* Catch-all redirect to dashboard */}
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </Router>
  );
}

export default App;
